import { focusSafely, getFocusableTreeWalker } from "@react-aria/focus";
import { usePress, isFocusVisible } from "@react-aria/interactions";
import { useSelectableCollection, useSelectableItem } from "@react-aria/selection";
import { useCollator, useLocale, useMessageFormatter } from "@react-aria/i18n";
import { useMemo, useRef } from "react";
import { filterDOMProps, mergeProps, useId, useUpdateEffect } from "@react-aria/utils";
import { announce } from "@react-aria/live-announcer";
import _babelRuntimeHelpersEsmInteropRequireDefault from "@babel/runtime/helpers/esm/interopRequireDefault";
import _babelRuntimeHelpersEsmExtends from "@babel/runtime/helpers/esm/extends";
import { Rect } from "@react-stately/virtualizer";
export class GridKeyboardDelegate {
  constructor(options) {
    this.collection = void 0;
    this.disabledKeys = void 0;
    this.ref = void 0;
    this.direction = void 0;
    this.collator = void 0;
    this.layout = void 0;
    this.focusMode = void 0;
    this.collection = options.collection;
    this.disabledKeys = options.disabledKeys;
    this.ref = options.ref;
    this.direction = options.direction;
    this.collator = options.collator;
    this.layout = options.layout;
    this.focusMode = options.focusMode || 'row';
  }

  isCell(node) {
    return node.type === 'cell';
  }

  isRow(node) {
    return node.type === 'row' || node.type === 'item';
  }

  findPreviousKey(fromKey, pred) {
    let key = fromKey != null ? this.collection.getKeyBefore(fromKey) : this.collection.getLastKey();

    while (key != null) {
      let item = this.collection.getItem(key);

      if (!pred || pred(item)) {
        return key;
      }

      key = this.collection.getKeyBefore(key);
    }
  }

  findNextKey(fromKey, pred) {
    let key = fromKey != null ? this.collection.getKeyAfter(fromKey) : this.collection.getFirstKey();

    while (key != null) {
      let item = this.collection.getItem(key);

      if (!pred || pred(item)) {
        return key;
      }

      key = this.collection.getKeyAfter(key);
    }
  }

  getKeyBelow(key) {
    let startItem = this.collection.getItem(key);

    if (!startItem) {
      return;
    } // If focus was on a cell, start searching from the parent row


    if (this.isCell(startItem)) {
      key = startItem.parentKey;
    } // Find the next item


    key = this.findNextKey(key);

    if (key != null) {
      // If focus was on a cell, focus the cell with the same index in the next row.
      if (this.isCell(startItem)) {
        let item = this.collection.getItem(key);
        return [...item.childNodes][startItem.index].key;
      } // Otherwise, focus the next row


      if (this.focusMode === 'row') {
        return key;
      }
    }
  }

  getKeyAbove(key) {
    let startItem = this.collection.getItem(key);

    if (!startItem) {
      return;
    } // If focus is on a cell, start searching from the parent row


    if (this.isCell(startItem)) {
      key = startItem.parentKey;
    } // Find the previous item


    key = this.findPreviousKey(key);

    if (key != null) {
      // If focus was on a cell, focus the cell with the same index in the previous row.
      if (this.isCell(startItem)) {
        let item = this.collection.getItem(key);
        return [...item.childNodes][startItem.index].key;
      } // Otherwise, focus the previous row


      if (this.focusMode === 'row') {
        return key;
      }
    }
  }

  getKeyRightOf(key) {
    let item = this.collection.getItem(key);

    if (!item) {
      return;
    } // If focus is on a row, focus the first child cell.


    if (this.isRow(item)) {
      let children = [...item.childNodes];
      return this.direction === 'rtl' ? children[children.length - 1].key : children[0].key;
    } // If focus is on a cell, focus the next cell if any,
    // otherwise focus the parent row.


    if (this.isCell(item)) {
      let parent = this.collection.getItem(item.parentKey);
      let children = [...parent.childNodes];
      let next = this.direction === 'rtl' ? children[item.index - 1] : children[item.index + 1];

      if (next) {
        return next.key;
      } // focus row only if focusMode is set to row


      if (this.focusMode === 'row') {
        return item.parentKey;
      }

      return this.direction === 'rtl' ? this.getFirstKey(key) : this.getLastKey(key);
    }
  }

  getKeyLeftOf(key) {
    let item = this.collection.getItem(key);

    if (!item) {
      return;
    } // If focus is on a row, focus the last child cell.


    if (this.isRow(item)) {
      let children = [...item.childNodes];
      return this.direction === 'rtl' ? children[0].key : children[children.length - 1].key;
    } // If focus is on a cell, focus the previous cell if any,
    // otherwise focus the parent row.


    if (this.isCell(item)) {
      let parent = this.collection.getItem(item.parentKey);
      let children = [...parent.childNodes];
      let prev = this.direction === 'rtl' ? children[item.index + 1] : children[item.index - 1];

      if (prev) {
        return prev.key;
      } // focus row only if focusMode is set to row


      if (this.focusMode === 'row') {
        return item.parentKey;
      }

      return this.direction === 'rtl' ? this.getLastKey(key) : this.getFirstKey(key);
    }
  }

  getFirstKey(key, global) {
    let item;

    if (key != null) {
      item = this.collection.getItem(key);

      if (!item) {
        return;
      } // If global flag is not set, and a cell is currently focused,
      // move focus to the first cell in the parent row.


      if (this.isCell(item) && !global) {
        let parent = this.collection.getItem(item.parentKey);
        return [...parent.childNodes][0].key;
      }
    } // Find the first row


    key = this.findNextKey(); // If global flag is set (or if focus mode is cell), focus the first cell in the first row.

    if (key != null && item && this.isCell(item) && global || this.focusMode === 'cell') {
      let item = this.collection.getItem(key);
      key = [...item.childNodes][0].key;
    } // Otherwise, focus the row itself.


    return key;
  }

  getLastKey(key, global) {
    let item;

    if (key != null) {
      item = this.collection.getItem(key);

      if (!item) {
        return;
      } // If global flag is not set, and a cell is currently focused,
      // move focus to the last cell in the parent row.


      if (this.isCell(item) && !global) {
        let parent = this.collection.getItem(item.parentKey);
        let children = [...parent.childNodes];
        return children[children.length - 1].key;
      }
    } // Find the last row


    key = this.findPreviousKey(); // If global flag is set (or if focus mode is cell), focus the last cell in the last row.

    if (key != null && item && this.isCell(item) && global || this.focusMode === 'cell') {
      let item = this.collection.getItem(key);
      let children = [...item.childNodes];
      key = children[children.length - 1].key;
    } // Otherwise, focus the row itself.


    return key;
  }

  getItem(key) {
    return this.ref.current.querySelector("[data-key=\"" + key + "\"]");
  }

  getItemRect(key) {
    if (this.layout) {
      var _this$layout$getLayou;

      return (_this$layout$getLayou = this.layout.getLayoutInfo(key)) == null ? void 0 : _this$layout$getLayou.rect;
    }

    let item = this.getItem(key);

    if (item) {
      return new Rect(item.offsetLeft, item.offsetTop, item.offsetWidth, item.offsetHeight);
    }
  }

  getPageHeight() {
    var _this$ref, _this$ref$current;

    if (this.layout) {
      var _this$layout$virtuali;

      return (_this$layout$virtuali = this.layout.virtualizer) == null ? void 0 : _this$layout$virtuali.visibleRect.height;
    }

    return (_this$ref = this.ref) == null ? void 0 : (_this$ref$current = _this$ref.current) == null ? void 0 : _this$ref$current.offsetHeight;
  }

  getContentHeight() {
    var _this$ref2, _this$ref2$current;

    if (this.layout) {
      return this.layout.getContentSize().height;
    }

    return (_this$ref2 = this.ref) == null ? void 0 : (_this$ref2$current = _this$ref2.current) == null ? void 0 : _this$ref2$current.scrollHeight;
  }

  getKeyPageAbove(key) {
    let itemRect = this.getItemRect(key);

    if (!itemRect) {
      return null;
    }

    let pageY = Math.max(0, itemRect.maxY - this.getPageHeight());

    while (itemRect && itemRect.y > pageY) {
      key = this.getKeyAbove(key);
      itemRect = this.getItemRect(key);
    }

    return key;
  }

  getKeyPageBelow(key) {
    let itemRect = this.getItemRect(key);

    if (!itemRect) {
      return null;
    }

    let pageHeight = this.getPageHeight();
    let pageY = Math.min(this.getContentHeight(), itemRect.y + pageHeight);

    while (itemRect && itemRect.maxY < pageY) {
      let nextKey = this.getKeyBelow(key);
      itemRect = this.getItemRect(nextKey); // Guard against case where maxY of the last key is barely less than pageY due to rounding
      // and thus it attempts to set key to null

      if (nextKey != null) {
        key = nextKey;
      }
    }

    return key;
  }

  getKeyForSearch(search, fromKey) {
    if (!this.collator) {
      return null;
    }

    let collection = this.collection;
    let key = fromKey != null ? fromKey : this.getFirstKey(); // If the starting key is a cell, search from its parent row.

    let startItem = collection.getItem(key);

    if (startItem.type === 'cell') {
      key = startItem.parentKey;
    }

    let hasWrapped = false;

    while (key != null) {
      let item = collection.getItem(key); // check row text value for match

      if (item.textValue) {
        let substring = item.textValue.slice(0, search.length);

        if (this.collator.compare(substring, search) === 0) {
          if (this.isRow(item) && this.focusMode === 'cell') {
            return [...item.childNodes][0].key;
          }

          return item.key;
        }
      }

      key = this.findNextKey(key); // Wrap around when reaching the end of the collection

      if (key == null && !hasWrapped) {
        key = this.getFirstKey();
        hasWrapped = true;
      }
    }

    return null;
  }

}

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
// Used to share keyboard delegate between useGrid and useGridCell
const $c7c16c1fc27e25a205a277212c8e7cfd$export$gridKeyboardDelegates = new WeakMap();
// ASSET: /Users/govett/dev/react-spectrum-v3/packages/@react-aria/grid/intl/en-US.json
var $c220079f51d67fa1d00cfe9981441af$exports = {};
$c220079f51d67fa1d00cfe9981441af$exports = JSON.parse("{\"selectedCount\":\"{count, plural, =0 {No items selected} one {# item selected} other {# items selected}}.\",\"selectedAll\":\"All items selected.\",\"selectedItem\":\"{item} selected.\",\"deselectedItem\":\"{item} not selected.\"}");
// @ts-ignore
const $e3c4cce891c32069b0b54eddd405e76$var$intlMessages = {
  "en-US": _babelRuntimeHelpersEsmInteropRequireDefault($c220079f51d67fa1d00cfe9981441af$exports).default
};

/**
 * Provides the behavior and accessibility implementation for a grid component.
 * A grid displays data in one or more rows and columns and enables a user to navigate its contents via directional navigation keys.
 * @param props - Props for the grid.
 * @param state - State for the grid, as returned by `useGridState`.
 * @param ref - The ref attached to the grid element.
 */
export function useGrid(props, state, ref) {
  let {
    isVirtualized,
    keyboardDelegate,
    focusMode,
    getRowText = key => {
      var _state$collection$get;

      return (_state$collection$get = state.collection.getItem(key)) == null ? void 0 : _state$collection$get.textValue;
    }
  } = props;
  let formatMessage = useMessageFormatter($e3c4cce891c32069b0b54eddd405e76$var$intlMessages);

  if (!props['aria-label'] && !props['aria-labelledby']) {
    console.warn('An aria-label or aria-labelledby prop is required for accessibility.');
  } // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
  // When virtualized, the layout object will be passed in as a prop and override this.


  let collator = useCollator({
    usage: 'search',
    sensitivity: 'base'
  });
  let {
    direction
  } = useLocale();
  let delegate = useMemo(() => keyboardDelegate || new GridKeyboardDelegate({
    collection: state.collection,
    disabledKeys: state.disabledKeys,
    ref,
    direction,
    collator,
    focusMode
  }), [keyboardDelegate, state.collection, state.disabledKeys, ref, direction, collator, focusMode]);
  let {
    collectionProps
  } = useSelectableCollection({
    ref,
    selectionManager: state.selectionManager,
    keyboardDelegate: delegate
  });
  let id = useId();
  $c7c16c1fc27e25a205a277212c8e7cfd$export$gridKeyboardDelegates.set(state, delegate);
  let domProps = filterDOMProps(props, {
    labelable: true
  });
  let gridProps = mergeProps(domProps, _babelRuntimeHelpersEsmExtends({
    role: 'grid',
    id,
    'aria-multiselectable': state.selectionManager.selectionMode === 'multiple' ? 'true' : undefined
  }, collectionProps));

  if (isVirtualized) {
    gridProps['aria-rowcount'] = state.collection.size;
    gridProps['aria-colcount'] = state.collection.columnCount;
  } // Many screen readers do not announce when items in a grid are selected/deselected.
  // We do this using an ARIA live region.


  let selection = state.selectionManager.rawSelection;
  let lastSelection = useRef(selection);
  useUpdateEffect(() => {
    if (!state.selectionManager.isFocused) {
      return;
    }

    let addedKeys = $e3c4cce891c32069b0b54eddd405e76$var$diffSelection(selection, lastSelection.current);
    let removedKeys = $e3c4cce891c32069b0b54eddd405e76$var$diffSelection(lastSelection.current, selection); // If adding or removing a single row from the selection, announce the name of that item.

    let messages = [];

    if (addedKeys.size === 1 && removedKeys.size === 0) {
      let addedText = getRowText(addedKeys.keys().next().value);

      if (addedText) {
        messages.push(formatMessage('selectedItem', {
          item: addedText
        }));
      }
    } else if (removedKeys.size === 1 && addedKeys.size === 0) {
      let removedText = getRowText(removedKeys.keys().next().value);

      if (removedText) {
        messages.push(formatMessage('deselectedItem', {
          item: removedText
        }));
      }
    } // Announce how many items are selected, except when selecting the first item.


    if (state.selectionManager.selectionMode === 'multiple') {
      if (messages.length === 0 || selection === 'all' || selection.size > 1 || lastSelection.current === 'all' || lastSelection.current.size > 1) {
        messages.push(selection === 'all' ? formatMessage('selectedAll') : formatMessage('selectedCount', {
          count: selection.size
        }));
      }
    }

    if (messages.length > 0) {
      announce(messages.join(' '));
    }

    lastSelection.current = selection;
  }, [selection]);
  return {
    gridProps
  };
}

function $e3c4cce891c32069b0b54eddd405e76$var$diffSelection(a, b) {
  let res = new Set();

  if (a === 'all' || b === 'all') {
    return res;
  }

  for (let key of a.keys()) {
    if (!b.has(key)) {
      res.add(key);
    }
  }

  return res;
}

/**
 * Provides the accessibility implementation for a row group in a grid.
 */
export function useGridRowGroup() {
  return {
    rowGroupProps: {
      role: 'rowgroup'
    }
  };
}

/**
 * Provides the behavior and accessibility implementation for a row in a grid.
 * @param props - Props for the row.
 * @param state - State of the parent grid, as returned by `useGridState`.
 */
export function useGridRow(props, state, ref) {
  let {
    node,
    isVirtualized,
    shouldSelectOnPressUp
  } = props;
  let {
    itemProps
  } = useSelectableItem({
    selectionManager: state.selectionManager,
    key: node.key,
    ref,
    isVirtualized,
    shouldSelectOnPressUp
  });
  let isSelected = state.selectionManager.isSelected(node.key);
  let isDisabled = state.disabledKeys.has(node.key); // TODO: move into useSelectableItem?

  let {
    pressProps
  } = usePress(_babelRuntimeHelpersEsmExtends({}, itemProps, {
    isDisabled
  }));

  let rowProps = _babelRuntimeHelpersEsmExtends({
    role: 'row',
    'aria-selected': state.selectionManager.selectionMode !== 'none' ? isSelected : undefined
  }, pressProps);

  if (isVirtualized) {
    rowProps['aria-rowindex'] = node.index + 1; // aria-rowindex is 1 based
  }

  return {
    rowProps
  };
}

/**
 * Provides the behavior and accessibility implementation for a cell in a grid.
 * @param props - Props for the cell.
 * @param state - State of the parent grid, as returned by `useGridState`.
 */
export function useGridCell(props, state, ref) {
  let {
    node,
    isVirtualized,
    focusMode = 'child',
    shouldSelectOnPressUp
  } = props;
  let {
    direction
  } = useLocale();
  let keyboardDelegate = $c7c16c1fc27e25a205a277212c8e7cfd$export$gridKeyboardDelegates.get(state); // Handles focusing the cell. If there is a focusable child,
  // it is focused, otherwise the cell itself is focused.

  let focus = () => {
    let treeWalker = getFocusableTreeWalker(ref.current);

    if (focusMode === 'child') {
      let focusable = state.selectionManager.childFocusStrategy === 'last' ? $c46764dc2253b1b174632a30143c6340$var$last(treeWalker) : treeWalker.firstChild();

      if (focusable) {
        focusSafely(focusable);
        return;
      }
    }

    if (!ref.current.contains(document.activeElement)) {
      focusSafely(ref.current);
    }
  };

  let {
    itemProps
  } = useSelectableItem({
    selectionManager: state.selectionManager,
    key: node.key,
    ref,
    isVirtualized,
    focus,
    shouldSelectOnPressUp
  }); // TODO: move into useSelectableItem?

  let isDisabled = state.disabledKeys.has(node.key) || state.disabledKeys.has(node.parentKey);
  let {
    pressProps
  } = usePress(_babelRuntimeHelpersEsmExtends({}, itemProps, {
    isDisabled
  }));

  let onKeyDown = e => {
    if (!e.currentTarget.contains(e.target)) {
      return;
    }

    let walker = getFocusableTreeWalker(ref.current);
    walker.currentNode = document.activeElement;

    switch (e.key) {
      case 'ArrowLeft':
        {
          // Find the next focusable element within the cell.
          let focusable = direction === 'rtl' ? walker.nextNode() : walker.previousNode(); // Don't focus the cell itself if focusMode is "child"

          if (focusMode === 'child' && focusable === ref.current) {
            focusable = null;
          }

          if (focusable) {
            e.preventDefault();
            e.stopPropagation();
            focusSafely(focusable);
          } else {
            // If there is no next focusable child, then move to the next cell to the left of this one.
            // This will be handled by useSelectableCollection. However, if there is no cell to the left
            // of this one, only one column, and the grid doesn't focus rows, then the next key will be the
            // same as this one. In that case we need to handle focusing either the cell or the first/last
            // child, depending on the focus mode.
            let prev = keyboardDelegate.getKeyLeftOf(node.key);

            if (prev !== node.key) {
              break;
            }

            e.preventDefault();
            e.stopPropagation();

            if (focusMode === 'cell' && direction === 'rtl') {
              focusSafely(ref.current);
            } else {
              walker.currentNode = ref.current;
              focusable = direction === 'rtl' ? walker.firstChild() : $c46764dc2253b1b174632a30143c6340$var$last(walker);

              if (focusable) {
                focusSafely(focusable);
              }
            }
          }

          break;
        }

      case 'ArrowRight':
        {
          let focusable = direction === 'rtl' ? walker.previousNode() : walker.nextNode();

          if (focusMode === 'child' && focusable === ref.current) {
            focusable = null;
          }

          if (focusable) {
            e.preventDefault();
            e.stopPropagation();
            focusSafely(focusable);
          } else {
            let next = keyboardDelegate.getKeyRightOf(node.key);

            if (next !== node.key) {
              break;
            }

            e.preventDefault();
            e.stopPropagation();

            if (focusMode === 'cell' && direction === 'ltr') {
              focusSafely(ref.current);
            } else {
              walker.currentNode = ref.current;
              focusable = direction === 'rtl' ? $c46764dc2253b1b174632a30143c6340$var$last(walker) : walker.firstChild();

              if (focusable) {
                focusSafely(focusable);
              }
            }
          }

          break;
        }

      case 'ArrowUp':
      case 'ArrowDown':
        // Prevent this event from reaching cell children, e.g. menu buttons. We want arrow keys to navigate
        // to the cell above/below instead. We need to re-dispatch the event from a higher parent so it still
        // bubbles and gets handled by useSelectableCollection.
        if (!e.altKey && ref.current.contains(e.target)) {
          e.stopPropagation();
          e.preventDefault();
          ref.current.parentElement.dispatchEvent(new KeyboardEvent(e.nativeEvent.type, e.nativeEvent));
        }

        break;
    }
  }; // Grid cells can have focusable elements inside them. In this case, focus should
  // be marshalled to that element rather than focusing the cell itself.


  let onFocus = e => {
    if (e.target !== ref.current) {
      // useSelectableItem only handles setting the focused key when
      // the focused element is the gridcell itself. We also want to
      // set the focused key when a child element receives focus.
      // If focus is currently visible (e.g. the user is navigating with the keyboard),
      // then skip this. We want to restore focus to the previously focused row/cell
      // in that case since the table should act like a single tab stop.
      if (!isFocusVisible()) {
        state.selectionManager.setFocusedKey(node.key);
      }

      return;
    } // If the cell itself is focused, wait a frame so that focus finishes propagatating
    // up to the tree, and move focus to a focusable child if possible.


    requestAnimationFrame(() => {
      if (focusMode === 'child' && document.activeElement === ref.current) {
        focus();
      }
    });
  };

  let gridCellProps = mergeProps(pressProps, {
    role: 'gridcell',
    onKeyDownCapture: onKeyDown,
    onFocus
  });

  if (isVirtualized) {
    gridCellProps['aria-colindex'] = node.index + 1; // aria-colindex is 1-based
  }

  return {
    gridCellProps
  };
}

function $c46764dc2253b1b174632a30143c6340$var$last(walker) {
  let next;
  let last;

  do {
    last = walker.lastChild();

    if (last) {
      next = last;
    }
  } while (last);

  return next;
}
//# sourceMappingURL=module.js.map
